import { t } from 'ember-intl';
import type { TOC } from '@ember/component/template-only';
import { PhArrowsClockwise, PhCloudArrowUp } from 'ember-phosphor-icons';
import Button from 'tio-ui/components/button';
import ReSelect from 'tio-ui/components/select';
import FileUploader from '../file-uploader.gts';
import {
  AI_READABLE_FILE_TYPES_DISPLAY,
  TA_MAX_UPLOAD_FILE_SIZE_BYTES,
} from '../../utils/tuition-assistance/ai-validation.ts';

const TA_MAX_UPLOAD_FILE_SIZE_MB = TA_MAX_UPLOAD_FILE_SIZE_BYTES / (1024 * 1024);

export interface AttachmentClassificationOption {
  label: string;
  key: string;
}

interface AttachmentUploadSectionSignature {
  Args: {
    canUpload: boolean;
    showUploadSection: boolean;
    toggleUploadSection: () => void;
    selectedClassification: string;
    classificationDidChange: (key: string) => void;
    uploadFile: { perform: (file: unknown) => Promise<void>; isRunning: boolean };
    classificationOptions?: AttachmentClassificationOption[];
    categoryOptions?: AttachmentClassificationOption[];
    selectedCategory?: string | null;
    categoryDidChange?: (key: string) => void;
    uploadDisabled?: boolean;
    /** File-picker filter (e.g. AI-readable types for categorized uploads). */
    accept?: string;
  };
}

const AttachmentUploadSection: TOC<AttachmentUploadSectionSignature> = <template>
  {{#if @canUpload}}
    {{#unless @showUploadSection}}
      <Button
        @intent="primary"
        @appearance="outlined"
        @icon={{PhCloudArrowUp}}
        @label={{t
          "common.tas.approval.drawer.attachments.upload_attachment"
          default="Upload Attachment"
        }}
        class="w-full"
        {{on "click" @toggleUploadSection}}
        data-test-upload-attachment-button
      />
    {{/unless}}
  {{/if}}

  {{#if @showUploadSection}}
    <div class="border app-divider-info rounded-lg tio-bg-surface shadow-sm">
      <div class="tio-row-between px-6 py-4 border-b app-divider">
        <div class="tio-row-tight">
          <PhCloudArrowUp @size="20" class="app-icon-info" />
          <h3 class="tio-text-base-semibold tio-fg-primary">
            {{t "tuition_assistance.program_details.upload_attachments"}}
          </h3>
        </div>
        <Button
          @appearance="minimal"
          @size="sm"
          @intent="default"
          {{on "click" @toggleUploadSection}}
          data-test-cancel-upload-button
        >
          {{t "common.cancel"}}
        </Button>
      </div>

      <div class="px-6 py-5 space-y-5">
        <FileUploader
          @didUploadFile={{@uploadFile.perform}}
          @accept={{@accept}}
          @enforceAccept={{true}}
          @maxFileSize={{TA_MAX_UPLOAD_FILE_SIZE_BYTES}}
          as |uploader|
        >
          {{! Lock both selectors for the whole transfer (S3 upload included) —
            the drawers read them when didUploadFile fires, so changing either
            mid-flight would tag the file under the wrong row. }}
          {{#let (or @uploadFile.isRunning uploader.uploadTask.isRunning) as |uploadBusy|}}
            {{#if @classificationOptions}}
              <div>
                <label class="tio-text-sm-medium tio-fg-secondary block mb-2">
                  {{t
                    "tuition_assistance.program_details.courses.document_type"
                    default="Document Type"
                  }}
                </label>
                <ReSelect
                  @items={{@classificationOptions}}
                  @selected={{@selectedClassification}}
                  @onChange={{@classificationDidChange}}
                  @clearable={{false}}
                  @disabled={{uploadBusy}}
                />
              </div>
            {{/if}}

            {{#if @categoryOptions.length}}
              <div data-test-category-select>
                <ReSelect
                  @label={{t
                    "common.tas.approval.drawer.attachments.document_category_required"
                    default="Document Category (required)"
                  }}
                  @items={{@categoryOptions}}
                  @selected={{@selectedCategory}}
                  @onChange={{@categoryDidChange}}
                  @clearable={{false}}
                  @disabled={{uploadBusy}}
                />
                {{#if @uploadDisabled}}
                  <p class="tio-text-caption mt-2" data-test-category-required-hint>
                    {{t
                      "common.tas.approval.drawer.attachments.select_category_to_upload"
                      default="Select a document category to enable upload"
                    }}
                  </p>
                {{/if}}
              </div>
            {{/if}}

            <div>
              <input
                id={{uploader.uniqueId}}
                type="file"
                multiple="true"
                accept={{@accept}}
                {{uploader.queue.selectFile}}
                class="hidden"
                disabled={{or uploadBusy @uploadDisabled}}
              />
              <label
                for={{uploader.uniqueId}}
                class="group app-dropzone block cursor-pointer rounded-lg
                  {{if (or uploadBusy @uploadDisabled) 'pointer-events-none cursor-not-allowed'}}
                  {{if @uploadDisabled 'opacity-60'}}"
              >
                <div class="px-6 py-8 text-center">
                  {{#if uploadBusy}}
                    <PhArrowsClockwise @size="32" class="mx-auto mb-3 app-icon-info animate-spin" />
                    <p class="tio-text-sm-medium tio-fg-secondary">
                      {{t "file_upload.uploading"}}
                    </p>
                  {{else}}
                    <PhCloudArrowUp @size="32" class="mx-auto mb-3 app-dropzone__icon" />
                    <div class="tio-row-center gap-2 mb-2">
                      <span class="tio-text-sm-semibold app-dropzone__accent">
                        {{t "common.choose_file"}}
                      </span>
                      <span class="text-sm tio-fg-muted">
                        {{t "file_upload.or_drag_and_drop"}}
                      </span>
                    </div>
                    <p class="tio-text-caption">
                      {{t
                        "tuition_assistance.program_details.courses.you_can_upload_multiple_attachments"
                      }}
                    </p>
                    <p class="tio-text-caption" data-test-attachment-upload-constraints>
                      {{AI_READABLE_FILE_TYPES_DISPLAY}}
                      ·
                      {{t
                        "file_upload.max_file_size"
                        maxSize=TA_MAX_UPLOAD_FILE_SIZE_MB
                        default="Max file size {maxSize}MB"
                      }}
                    </p>
                  {{/if}}
                </div>
              </label>
            </div>
          {{/let}}
        </FileUploader>
      </div>
    </div>
  {{/if}}
</template>;

export default AttachmentUploadSection;
